/**
 * The arithmetic behind a data centre's Analytics (`DataCentreAnalytics.tsx`): which bucket a
 * period is drawn in, which series a chart reads, and how a device's several series become one
 * line. Pure, so every rule is tested without a screen.
 *
 * KEYED ON DEVICE ROLE AND READING KEY, never on a device's name — the same rule as the Overview's
 * (`modules/overview/data-centre.ts`).
 */
import type { LatestRow, RangeResponse } from '@/lib/api/types';
import type { ChartPoint, ChartSeries } from '@/components/charts/TimeSeriesChart';
import { breakAtGaps } from '@/components/charts/break-on-silence';
import { bucketMsOf } from '@/components/charts/no-data';
import { pduLabel } from '@/modules/overview/pdu-label';

export type Bucket = '15 minutes' | '1 hour' | '1 day';

export const BUCKET_SECONDS: Record<Bucket, number> = { '15 minutes': 900, '1 hour': 3600, '1 day': 86_400 };

/** Fine enough to see a step change in a day, coarse enough that a month stays a trace. */
export function bucketFor(fromMs: number, toMs: number): Bucket {
  const days = (toMs - fromMs) / 86_400_000;
  return days <= 2 ? '15 minutes' : days <= 14 ? '1 hour' : '1 day';
}

/** One line on a chart: a device's series of one key, summed or averaged across its instances. */
export interface Line {
  id: string;
  label: string;
  seriesIds: number[];
  how: 'sum' | 'mean';
  /**
   * A generator's output, counted only while its engine turns (`engineTurning`): the engine's
   * speed and output frequency series, read in the same buckets. A bucket in which neither says
   * the engine ran reads 0; one in which neither was reported keeps the value as it is.
   */
  whileRunning?: { speed: number[]; hz: number[] };
}

/**
 * Whether a generator's engine is turning, from its speed (rpm) and output frequency (Hz). Null
 * when neither is reported.
 *
 * THE OUTPUT REGISTER IS NOT PROOF (2026-10-20). Prod's DSE7320 reported 102 kW on
 * `generator_total_power` all night while the engine stood still — a run of 0 minutes that day —
 * because the controller's current transformers sit on the load side and read the load whichever
 * source carries it. The Load chart drew it as "Generator 102 kW" and the running rule called it
 * running. An engine that is not turning carries nothing, whatever the register says.
 */
export function engineTurning(speed: number | null, hz: number | null): boolean | null {
  if (speed === null && hz === null) return null;
  return (speed ?? 0) > 0 || (hz ?? 0) > 10;
}

export interface RoleDevice {
  id: number;
  name: string;
  role: string | null;
}

/**
 * One line per device of the given roles that reports `key`, or, for a device that does not, `also`
 * (a PDU's power factor per phase where a meter sends a total): never both for one device.
 */
export function linesFor(
  rows: readonly LatestRow[],
  devices: readonly RoleDevice[],
  roles: readonly string[],
  key: string,
  how: Line['how'],
  also?: string,
): Line[] {
  const of = (id: number, k: string): number[] => rows.filter((r) => r.deviceId === id && r.baseKey === k).map((r) => r.seriesId);
  const pduIds = devices.filter((d) => d.role === 'pdu').map((d) => d.id);
  return devices
    .filter((d) => roles.includes(d.role ?? ''))
    .map((d) => {
      const own = of(d.id, key);
      /* A PDU by the name every DC screen gives it (`pduLabel`), never the vendor's three lines. */
      return { id: `${d.id}:${key}`, label: d.role === 'pdu' ? pduLabel(pduIds, d.id, null) : d.name, seriesIds: own.length > 0 || also === undefined ? own : of(d.id, also), how };
    })
    .filter((l) => l.seriesIds.length > 0);
}

/**
 * The lines as chart series. A bucket some of a line's series missed is a GAP, never a partial sum:
 * a meter that skipped a poll must not read as the load falling.
 *
 * ONLY WHAT WAS READ (Hamzah, 2026-10-20, `components/charts/no-data.ts`). Two ways this used to show
 * more than the meters said, both closed here:
 *   · a series with no reading AT ALL in the period was left out of its line, so the line summed
 *     the rest: an incomer silent all week made Grid the other incomer alone. A line now counts
 *     every series it names, read or not.
 *   · a range read returns only the buckets that read something, so a silence was an ABSENCE, and a
 *     chart joined the bucket before it to the one after with a straight line (the bank's Load chart
 *     drew 17 Oct to 19 Oct as a smooth slope while nothing was read). A null now stands at the first
 *     bucket missed, at the range's own bucket size, so every line breaks there.
 */
export function toChartSeries(range: RangeResponse | undefined, lines: readonly Line[], unit: string | null): ChartSeries[] {
  const byId = new Map((range?.series ?? []).map((s) => [s.seriesId, s]));
  /* The highest of some series at one bucket, for the running gate. */
  const at = (ids: readonly number[], t: number): number | null => {
    let best: number | null = null;
    for (const id of ids) {
      const p = byId.get(id)?.points.find((x) => Date.parse(x.t) === t);
      if (p && p.v !== null && (best === null || p.v > best)) best = p.v;
    }
    return best;
  };
  const bucketMs = bucketMsOf(range?.bucket);
  const out: ChartSeries[] = [];
  for (const line of lines) {
    const want = [...new Set(line.seriesIds)];
    const got = want.map((id) => byId.get(id)).filter((s): s is NonNullable<typeof s> => s !== undefined);
    if (got.length === 0) continue;
    const byT = new Map<number, number[]>();
    for (const s of got) {
      for (const p of s.points) {
        if (p.v === null) continue;
        const t = Date.parse(p.t);
        byT.set(t, [...(byT.get(t) ?? []), p.v]);
      }
    }
    const points: ChartPoint[] = [...byT.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([t, vs]) => {
        if (vs.length < want.length) return { t, v: null };
        const total = vs.reduce((a, b) => a + b, 0);
        const v = line.how === 'sum' ? total : total / vs.length;
        if (line.whileRunning && engineTurning(at(line.whileRunning.speed, t), at(line.whileRunning.hz, t)) === false) return { t, v: 0 };
        return { t, v };
      });
    out.push({ id: line.id, label: line.label, unit, points: bucketMs === null ? points : breakAtGaps(points, (p) => p.t, bucketMs, (t) => ({ t, v: null })) });
  }
  return out;
}

/**
 * ONE LINE FOR A MEASURE THAT DOES NOT ADD UP (Hamzah, 2026-10-26: "Totals and Each feeder go away
 * when I click Current or Voltage"). Volts, amps, power factor and hertz have no total, so the Load
 * card's first choice reads the feeders' lines it would otherwise draw one by one: the lowest of them
 * (voltage, power factor), the highest (current), or their mean (frequency, which every feeder
 * shares). It is the envelope of exactly the lines Each feeder draws, never a second read.
 *
 * Every member or nothing, as a total is (`supplyNow`): a bucket one feeder missed is a gap, because
 * the feeder that did not report may be the lowest one. Null with no feeder line.
 */
export function summaryOf(lines: readonly ChartSeries[], how: 'min' | 'max' | 'mean', id: string, label: string): ChartSeries | null {
  const first = lines[0];
  if (first === undefined) return null;
  const at = lines.map((s) => new Map(s.points.map((p) => [p.t, p.v])));
  const times = [...new Set(lines.flatMap((s) => s.points.map((p) => p.t)))].sort((a, b) => a - b);
  const points: ChartPoint[] = times.map((t) => {
    const vs = at.map((m) => m.get(t) ?? null);
    if (vs.some((v) => v === null)) return { t, v: null };
    const ns = vs as number[];
    return { t, v: how === 'min' ? Math.min(...ns) : how === 'max' ? Math.max(...ns) : ns.reduce((a, b) => a + b, 0) / ns.length };
  });
  return { id, label, unit: first.unit, points };
}

/**
 * Energy per day from a cumulative kWh counter: the day's highest reading less its lowest. Read
 * from two day-bucketed queries (`max` and `min`). A day with one reading, or a counter that went
 * backwards (a meter reset or replaced), is a gap, never a negative day.
 */
export function dailyEnergy(maxRange: RangeResponse | undefined, minRange: RangeResponse | undefined, lines: readonly Line[]): ChartSeries[] {
  const mins = new Map((minRange?.series ?? []).map((s) => [s.seriesId, new Map(s.points.map((p) => [p.t, p.v]))]));
  const derived: RangeResponse | undefined = maxRange
    ? {
        ...maxRange,
        series: maxRange.series.map((s) => ({
          ...s,
          points: s.points.map((p) => {
            const lo = mins.get(s.seriesId)?.get(p.t) ?? null;
            const d = p.v === null || lo === null ? null : p.v - lo;
            return { t: p.t, v: d === null || d < 0 ? null : d };
          }),
        })),
      }
    : undefined;
  return toChartSeries(derived, lines, 'kWh');
}

/** One feeder's figures over a period, for the drill-down table. */
export interface FeederPeriod {
  deviceId: number;
  avgKw: number | null;
  peakKw: number | null;
  kwh: number | null;
  minV: number | null;
  maxV: number | null;
  minPf: number | null;
}

const values = (range: RangeResponse | undefined, ids: readonly number[]): number[] =>
  (range?.series ?? [])
    .filter((s) => ids.includes(s.seriesId))
    .flatMap((s) => s.points.map((p) => p.v))
    .filter((v): v is number => v !== null);

/**
 * Each bucket's values across `ids` ADDED, one per bucket in which every series reported: a bucket
 * some series missed is left out (a gap), never a partial sum that reads as the load falling.
 */
function summedBuckets(range: RangeResponse | undefined, ids: readonly number[]): number[] {
  const series = (range?.series ?? []).filter((s) => ids.includes(s.seriesId));
  if (series.length === 0) return [];
  const byT = new Map<string, { n: number; v: number }>();
  for (const s of series) {
    for (const p of s.points) {
      if (p.v === null) continue;
      const cur = byT.get(p.t) ?? { n: 0, v: 0 };
      byT.set(p.t, { n: cur.n + 1, v: cur.v + p.v });
    }
  }
  return [...byT.values()].filter((x) => x.n === series.length).map((x) => x.v);
}

/**
 * Counters' advance over the window: EACH counter's highest less its own lowest, then added. Null
 * when any counter has no reading in the window, because a total missing a counter is not the
 * feeder's energy. Never one counter's highest less another's lowest: two PDUs behind one agent are
 * two counters (221,552 and 138,636 kWh), and that subtraction printed 82,979 kWh for one day.
 */
function counterAdvance(hiR: RangeResponse | undefined, loR: RangeResponse | undefined, ids: readonly number[]): number | null {
  if (ids.length === 0) return null;
  let total = 0;
  for (const id of ids) {
    const hi = values(hiR, [id]);
    const lo = values(loR, [id]);
    if (hi.length === 0 || lo.length === 0) return null;
    total += Math.max(0, Math.max(...hi) - Math.min(...lo));
  }
  return total;
}

/**
 * A feeder's period figures from bucketed reads: the mean of its power buckets, the highest bucket
 * peak, its import counter's advance, and its voltage and power-factor extremes. Each is null when
 * the period holds no reading of it — never a zero that reads as a measurement.
 *
 * A FEEDER OF SEVERAL METERS (a combine's two channels, two PDUs behind one agent) is their SUM:
 * power added bucket by bucket (`summedBuckets`), each counter advanced on its own
 * (`counterAdvance`). Its peak is the highest bucket MAX of a single meter, but the highest summed
 * bucket MEAN of several: two meters' maxima can fall at different moments in a bucket, and adding
 * them would print a peak the feeder never drew.
 */
export function feederPeriod(
  deviceId: number,
  ids: { kw: number[]; kwh: number[]; volts: number[]; pf: number[] },
  q: { kwAvg?: RangeResponse; kwMax?: RangeResponse; kwhMax?: RangeResponse; kwhMin?: RangeResponse; vMin?: RangeResponse; vMax?: RangeResponse; pfMin?: RangeResponse },
): FeederPeriod {
  const avg = summedBuckets(q.kwAvg, ids.kw);
  const peak = ids.kw.length === 1 ? values(q.kwMax, ids.kw) : avg;
  const vlo = values(q.vMin, ids.volts);
  const vhi = values(q.vMax, ids.volts);
  const pf = values(q.pfMin, ids.pf);
  return {
    deviceId,
    avgKw: avg.length ? avg.reduce((a, b) => a + b, 0) / avg.length : null,
    peakKw: peak.length ? Math.max(...peak) : null,
    kwh: counterAdvance(q.kwhMax, q.kwhMin, ids.kwh),
    minV: vlo.length ? Math.min(...vlo) : null,
    maxV: vhi.length ? Math.max(...vhi) : null,
    minPf: pf.length ? Math.min(...pf) : null,
  };
}

/** What the Feeders chart draws: each feeder's power, its line-to-line voltage, or its power factor. */
export type FeederMeasure = 'kw' | 'volts' | 'pf';

/** The bands the Feeders chart marks against, the Overview's own (`data-centre.ts`, its cited sources). */
export const FEEDER_VOLTS_BAND = [360, 424] as const;
export const FEEDER_PF_FLOOR = 0.85;

export interface FeederStat {
  label: string;
  value: string;
  unit?: string;
  sub?: string;
  tone?: 'warn';
}

/**
 * THE FEEDERS CHART'S FIGURES (2026-10-27, Hamzah: "convert into the graph"; the table this replaced held
 * them in seven columns): the period's figures of ONE feeder, for the measure on the chart, riding on it
 * as its stats. Power: mean, peak and energy; voltage: lowest and highest against the band; power factor:
 * the lowest against its floor. A figure the period holds no reading of is left out, never a dash; a
 * figure past its band says so in its tone, never by colour alone (the sub names the band). `whose` names
 * the feeder where the chart draws several, so a figure is never read as the whole site's.
 */
export function feederStats(p: FeederPeriod, measure: FeederMeasure, whose: string | null, share: number | null): FeederStat[] {
  const of = (label: string): string => (whose ? `${whose}, ${label.toLowerCase()}` : label);
  const read = share !== null && share < 1 ? `read ${Math.round(share * 100)}% of the period` : undefined;
  const out: FeederStat[] = [];
  const fig = (v: number, digits?: number): string => (digits === undefined ? (Number.isInteger(v) || Math.abs(v) >= 100 ? Math.round(v).toLocaleString('en-GB') : v.toFixed(Math.abs(v) >= 10 ? 0 : 1)) : v.toFixed(digits));
  if (measure === 'kw') {
    if (p.avgKw !== null) out.push({ label: of('Mean'), value: fig(p.avgKw), unit: 'kW', ...(read ? { sub: read } : {}) });
    if (p.peakKw !== null) out.push({ label: of('Peak'), value: fig(p.peakKw), unit: 'kW' });
    if (p.kwh !== null) out.push({ label: of('Energy'), value: fig(p.kwh), unit: 'kWh' });
  } else if (measure === 'volts') {
    const band = `band ${FEEDER_VOLTS_BAND[0]} to ${FEEDER_VOLTS_BAND[1]} V`;
    if (p.minV !== null) out.push({ label: of('Lowest'), value: p.minV.toFixed(0), unit: 'V', sub: band, ...(p.minV < FEEDER_VOLTS_BAND[0] ? { tone: 'warn' as const } : {}) });
    if (p.maxV !== null) out.push({ label: of('Highest'), value: p.maxV.toFixed(0), unit: 'V', sub: band, ...(p.maxV > FEEDER_VOLTS_BAND[1] ? { tone: 'warn' as const } : {}) });
  } else if (p.minPf !== null) {
    out.push({ label: of('Lowest'), value: p.minPf.toFixed(2), sub: `floor ${FEEDER_PF_FLOOR}`, ...(p.minPf < FEEDER_PF_FLOOR ? { tone: 'warn' as const } : {}) });
  }
  return out;
}


/**
 * The days an energy-per-day chart draws, and which of them it can total.
 *
 * EVERY DAY BETWEEN THE FIRST AND LAST READ, AND A TOTAL ONLY WHERE EVERY INCOMER WAS READ (Hamzah,
 * 2026-10-20). The chart drew only the days something was read on, so NBR's silent 18 Oct vanished
 * and 17 and 19 Oct stood side by side; and a day one incomer missed was stacked from the other
 * alone, its label a smaller total. Days before the first reading are still left out (a period that
 * starts before the site's history is not a row of empty days), but a day inside it nobody read
 * stays, hatched, and a day short of an incomer is a gap, never a smaller sum.
 */
export function energyDays(
  perLine: ReadonlyArray<{ byDay: ReadonlyMap<string, number | null> }>,
  days: readonly string[],
): { shown: string[]; complete: Set<string> } {
  const any = (d: string): boolean => perLine.some((l) => (l.byDay.get(d) ?? null) !== null);
  const first = days.findIndex(any);
  if (first < 0 || perLine.length === 0) return { shown: [], complete: new Set() };
  let last = days.length - 1;
  while (last > first && !any(days[last] as string)) last--;
  const shown = days.slice(first, last + 1);
  return { shown, complete: new Set(shown.filter((d) => perLine.every((l) => (l.byDay.get(d) ?? null) !== null))) };
}

/**
 * How much of a period a set of series was read in: the share of its buckets in which every one
 * reported. Null when all but a twentieth was, so a whole period is said plainly; 0 when none was.
 */
export function readShare(range: RangeResponse | undefined, ids: readonly number[], from: number, to: number, bucketMs: number): number | null {
  if (ids.length === 0 || to <= from || bucketMs <= 0) return null;
  const counts = new Map<number, number>();
  for (const s of range?.series ?? []) {
    if (!ids.includes(s.seriesId)) continue;
    for (const p of new Set(s.points.filter((x) => x.v !== null).map((x) => Date.parse(x.t)))) counts.set(p, (counts.get(p) ?? 0) + 1);
  }
  const want = new Set(ids).size;
  const read = [...counts.values()].filter((n) => n >= want).length;
  const share = Math.min(1, (read * bucketMs) / (to - from));
  return share >= 0.95 ? null : share;
}

/**
 * One line per INSTANCE of a key on one device: its three phases, labelled "Phase 1" and so on. A
 * device of several units behind one agent (two PDUs: their rows carry a negative per-unit meter,
 * `dc-vocabulary.ts`) names the unit too, "Unit 2, phase 1", so two lines never share a label.
 */
export function phaseLines(rows: readonly LatestRow[], deviceId: number, key: string): Line[] {
  const own = rows.filter((r) => r.deviceId === deviceId && r.baseKey === key);
  const units = [...new Set(own.map((r) => r.sourceDeviceId ?? 0).filter((u) => u < 0))].sort((a, b) => b - a);
  const unitOf = (r: LatestRow): number | null => (units.length > 1 ? units.indexOf(r.sourceDeviceId ?? 0) + 1 : null);
  return own
    .sort((a, b) => (unitOf(a) ?? 0) - (unitOf(b) ?? 0) || a.instance - b.instance)
    .map((r) => {
      const unit = unitOf(r);
      const phase = r.instance > 0 ? `Phase ${r.instance}` : 'Total';
      return {
        id: `${deviceId}:${key}:${unit ?? 0}:${r.instance}`,
        label: unit === null ? phase : `Unit ${unit}, ${phase.toLowerCase()}`,
        seriesIds: [r.seriesId],
        how: 'mean' as const,
      };
    });
}
